import {
  BillFrequency,
  ChildEducationAnswer,
  DebtAnswer,
  OnboardingAnswers,
  RecurringAmount,
  UtilityAnswer
} from './onboardingCategoryBuilder';
import { ExchangeRates, convertToTargetCurrency } from './exchangeRates';

export interface BudgetSummary {
  totalIncome: number;
  totalCosts: number;
  totalDebtPayments: number;
  balance: number;
  /** Income minus costs minus monthly debt payments */
  surplusAfterDebts: number;
  /** True when there is room to save after covering costs and debt payments */
  canSaveForGoals: boolean;
  currency: string;
  /** True when costs meet or exceed income and at least one cost was entered */
  isOverBudget: boolean;
}

interface AmountEntry {
  amount: number;
  currency: string;
  frequency: BillFrequency;
}

function monthlyEquivalent(amount: number, frequency: BillFrequency): number {
  if (frequency === 'yearly') return amount / 12;
  if (frequency === 'quarterly') return amount / 3;
  return amount;
}

function addRecurring(entries: AmountEntry[], bill: RecurringAmount, defaultCurrency: string): void {
  if (bill.amount == null || bill.amount <= 0) return;
  entries.push({
    amount: bill.amount,
    currency: bill.currency || defaultCurrency,
    frequency: bill.frequency
  });
}

function addUtility(entries: AmountEntry[], utility: UtilityAnswer, defaultCurrency: string): void {
  if (!utility.enabled) return;
  addRecurring(entries, utility.bill, defaultCurrency);
}

function collectCostEntries(answers: OnboardingAnswers): AmountEntry[] {
  const defaultCurrency = answers.primaryCurrency || 'USD';
  const entries: AmountEntry[] = [];

  answers.rentalUnits.forEach(unit => {
    if (unit.housingType !== 'own') {
      addRecurring(entries, unit.payment, defaultCurrency);
    }
  });

  answers.livingLocations.forEach(location => {
    addRecurring(entries, location.groceries, defaultCurrency);
    addRecurring(entries, location.transportation, defaultCurrency);
    addUtility(entries, location.electricity, defaultCurrency);
    addUtility(entries, location.water, defaultCurrency);
    addUtility(entries, location.internet, defaultCurrency);
    addUtility(entries, location.phone, defaultCurrency);
  });

  answers.cars.forEach(car => {
    addRecurring(entries, car.fuel, defaultCurrency);
    addRecurring(entries, car.insurance, defaultCurrency);
    addRecurring(entries, car.maintenance, defaultCurrency);
    addRecurring(entries, car.loan, defaultCurrency);
  });

  answers.properties.forEach(property => {
    if (property.hasInstallments) addRecurring(entries, property.installments, defaultCurrency);
    if (property.hasMaintenance) addRecurring(entries, property.maintenance, defaultCurrency);
    if (property.hasPropertyTax) addRecurring(entries, property.propertyTax, defaultCurrency);
    if (property.hasInsurance) addRecurring(entries, property.insurance, defaultCurrency);
    if (property.rentsOut) addRecurring(entries, property.rentalExpenses, defaultCurrency);
  });

  answers.children.forEach(child => {
    addChildEducationCosts(entries, child, defaultCurrency);
  });

  if (answers.includeSubscriptions) {
    addRecurring(entries, answers.subscriptionBill, defaultCurrency);
  }

  return entries;
}

function addChildEducationCosts(
  entries: AmountEntry[],
  child: ChildEducationAnswer,
  defaultCurrency: string
): void {
  if (child.avgSchoolYearly && child.avgSchoolYearly > 0) {
    entries.push({
      amount: child.avgSchoolYearly,
      currency: child.avgSchoolCurrency || defaultCurrency,
      frequency: 'yearly'
    });
  }
  if (child.avgUniversityYearly && child.avgUniversityYearly > 0) {
    entries.push({
      amount: child.avgUniversityYearly,
      currency: child.avgUniversityCurrency || defaultCurrency,
      frequency: 'yearly'
    });
  }
  addRecurring(entries, child.supplies, defaultCurrency);
  addRecurring(entries, child.activities, defaultCurrency);
}

function collectIncomeEntries(answers: OnboardingAnswers): AmountEntry[] {
  const defaultCurrency = answers.primaryCurrency || 'USD';
  const entries: AmountEntry[] = [];

  answers.incomeSources.forEach(source => {
    if (source.expectedMonthly == null || source.expectedMonthly <= 0) return;
    entries.push({
      amount: source.expectedMonthly,
      currency: source.expectedCurrency || defaultCurrency,
      frequency: 'monthly'
    });
  });

  return entries;
}

function collectDebtPaymentEntries(answers: OnboardingAnswers): AmountEntry[] {
  const defaultCurrency = answers.primaryCurrency || 'USD';
  const entries: AmountEntry[] = [];

  (answers.debts ?? []).forEach((debt: DebtAnswer) => {
    addRecurring(entries, debt.payment, debt.totalOwedCurrency || defaultCurrency);
  });

  return entries;
}

function sumEntries(
  entries: AmountEntry[],
  targetCurrency: string,
  exchangeRates: ExchangeRates | null
): number {
  return entries.reduce((total, entry) => {
    const converted =
      exchangeRates != null
        ? convertToTargetCurrency(entry.amount, entry.currency, targetCurrency, exchangeRates)
        : entry.currency === targetCurrency
          ? entry.amount
          : null;

    if (converted == null) return total;
    return total + monthlyEquivalent(converted, entry.frequency);
  }, 0);
}

/**
 * Compute monthly income, costs, and remaining balance in the budget currency.
 * When exchange rates are unavailable, only same-currency amounts are counted.
 */
export function computeBudgetSummary(
  answers: OnboardingAnswers,
  budgetCurrency: string,
  exchangeRates: ExchangeRates | null = null
): BudgetSummary {
  const currency = budgetCurrency || answers.primaryCurrency || 'USD';
  const incomeEntries = collectIncomeEntries(answers);
  const costEntries = collectCostEntries(answers);
  const debtEntries = collectDebtPaymentEntries(answers);

  const totalIncome = sumEntries(incomeEntries, currency, exchangeRates);
  const totalCosts = sumEntries(costEntries, currency, exchangeRates);
  const totalDebtPayments = sumEntries(debtEntries, currency, exchangeRates);
  const balance = totalIncome - totalCosts;
  const surplusAfterDebts = balance - totalDebtPayments;

  return {
    totalIncome,
    totalCosts,
    totalDebtPayments,
    balance,
    surplusAfterDebts,
    canSaveForGoals: surplusAfterDebts > 0,
    currency,
    isOverBudget: totalCosts > 0 && balance <= 0
  };
}

/** Round for display (2 decimal places, strip trailing zeros for integers). */
export function formatBudgetAmount(amount: number): string {
  if (!Number.isFinite(amount)) return '0';
  const rounded = Math.round(amount * 100) / 100;
  return Number.isInteger(rounded)
    ? rounded.toLocaleString()
    : rounded.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
